import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk';
import { useLanguage } from '../config/language';
import { 
  Shield, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Camera, 
  FileText, 
  User,
  ArrowLeft,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function IdentityVerification() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  
  const [step, setStep] = useState('intro'); // intro, creating, verifying, success, failed
  const [sessionId, setSessionId] = useState(null);
  const [verificationUrl, setVerificationUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // User data (get from auth context or form)
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    id_number: ''
  });

  // Check verification status periodically
  useEffect(() => {
    let interval;
    if (sessionId && (step === 'verifying' || step === 'waiting')) {
      interval = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE}/api/verification/status/${sessionId}`);
          const data = await response.json();
          
          if (data.status === 'approved') {
            setVerificationResult(data);
            setStep('success');
            clearInterval(interval);
          } else if (data.status === 'declined') {
            setVerificationResult(data);
            setStep('failed');
            clearInterval(interval);
          } else if (data.status === 'expired') {
            setError(isRTL ? 'انتهت صلاحية الجلسة' : 'Session expired');
            setStep('failed');
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Error checking status:', err);
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [sessionId, step, isRTL]);

  const createSession = async () => {
    if (!userData.first_name || !userData.last_name || !userData.email) {
      setError(isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    setStep('creating');
    
    try {
      const response = await fetch(`${API_BASE}/api/verification/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          phone: userData.phone || undefined,
          id_number: userData.id_number || undefined
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create session');
      }
      
      const data = await response.json();
      setSessionId(data.session_id);
      setVerificationUrl(data.verification_url);
      setStep('ready');
      
    } catch (err) {
      setError(isRTL ? 'فشل في إنشاء جلسة التوثيق' : 'Failed to create verification session');
      setStep('intro');
    } finally {
      setLoading(false);
    }
  };

  const launchVerification = () => {
    if (!verificationUrl) return;
    
    setStep('verifying');
    
    createVeriffFrame({
      url: verificationUrl,
      onEvent: (msg) => {
        switch(msg) {
          case MESSAGES.STARTED:
            console.log('Verification started');
            break;
          case MESSAGES.FINISHED:
            console.log('Verification finished');
            setStep('waiting');
            break;
          case MESSAGES.CANCELED:
            console.log('Verification canceled');
            setStep('ready');
            break;
          default:
            console.log('Veriff event:', msg);
        }
      }
    });
  };

  const texts = {
    ar: {
      title: 'توثيق الهوية',
      subtitle: 'قم بتوثيق هويتك للوصول إلى جميع الخدمات',
      step1: 'تصوير الهوية أو جواز السفر',
      step1Desc: 'سيتم قراءة بياناتك تلقائياً',
      step2: 'التقاط صورة شخصية',
      step2Desc: 'للتحقق من مطابقة الوجه',
      step3: 'اختبار الحيوية',
      step3Desc: 'للتأكد أنك شخص حقيقي',
      startBtn: 'بدء التوثيق',
      continueBtn: 'متابعة التوثيق',
      firstName: 'الاسم الأول',
      lastName: 'اسم العائلة',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف (اختياري)',
      idNumber: 'رقم الهوية الإماراتية (اختياري)',
      creating: 'جاري إنشاء جلسة التوثيق...',
      verifying: 'جاري التوثيق...',
      waiting: 'جاري مراجعة البيانات...',
      successTitle: 'تم التوثيق بنجاح!',
      successMsg: 'تم التحقق من هويتك بنجاح. يمكنك الآن الوصول إلى جميع الخدمات.',
      failedTitle: 'فشل التوثيق',
      failedMsg: 'لم نتمكن من التحقق من هويتك. يرجى المحاولة مرة أخرى.',
      tryAgain: 'حاول مرة أخرى',
      goToDashboard: 'الذهاب للوحة التحكم',
      back: 'رجوع'
    },
    en: {
      title: 'Identity Verification',
      subtitle: 'Verify your identity to access all services',
      step1: 'Scan ID or Passport',
      step1Desc: 'Your data will be read automatically',
      step2: 'Take a Selfie',
      step2Desc: 'To verify face match',
      step3: 'Liveness Check',
      step3Desc: 'To confirm you are a real person',
      startBtn: 'Start Verification',
      continueBtn: 'Continue Verification',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone (optional)',
      idNumber: 'Emirates ID (optional)',
      creating: 'Creating verification session...',
      verifying: 'Verification in progress...',
      waiting: 'Reviewing your data...',
      successTitle: 'Verification Successful!',
      successMsg: 'Your identity has been verified. You can now access all services.',
      failedTitle: 'Verification Failed',
      failedMsg: 'We could not verify your identity. Please try again.',
      tryAgain: 'Try Again',
      goToDashboard: 'Go to Dashboard',
      back: 'Back'
    }
  };
  
  const t = texts[language] || texts.ar;

  // Render different states
  if (step === 'creating' || step === 'verifying' || step === 'waiting') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center p-8">
          <Loader2 className="h-16 w-16 text-[#1E9BD9] mx-auto animate-spin mb-6" />
          <h2 className="text-xl font-bold text-[#0F172A] mb-2">
            {step === 'creating' && t.creating}
            {step === 'verifying' && t.verifying}
            {step === 'waiting' && t.waiting}
          </h2>
          <p className="text-[#64748B]">
            {isRTL ? 'يرجى الانتظار...' : 'Please wait...'}
          </p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-md w-full mx-4 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-3">{t.successTitle}</h2>
            <p className="text-[#64748B] mb-8">{t.successMsg}</p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="w-full btn-primary text-white"
            >
              {t.goToDashboard}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-md w-full mx-4 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-3">{t.failedTitle}</h2>
            <p className="text-[#64748B] mb-8">{t.failedMsg}</p>
            <Button 
              onClick={() => {
                setStep('intro');
                setSessionId(null);
                setVerificationUrl(null);
              }}
              className="w-full btn-primary text-white"
            >
              {t.tryAgain}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Ready state - launch verification
  if (step === 'ready') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white py-12 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-[#E0F2FE] rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="h-12 w-12 text-[#1E9BD9]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-3">{t.title}</h2>
            <p className="text-[#64748B] mb-8">
              {isRTL ? 'جلسة التوثيق جاهزة. اضغط للمتابعة.' : 'Verification session is ready. Click to continue.'}
            </p>
            
            <Button 
              onClick={launchVerification}
              className="w-full btn-primary text-white py-4 text-lg"
              data-testid="launch-verification-btn"
            >
              {t.continueBtn}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Intro state - form
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-white py-12 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6 transition-colors"
        >
          <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
          <span>{t.back}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#E0F2FE] rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-12 w-12 text-[#1E9BD9]" />
          </div>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">{t.title}</h1>
          <p className="text-[#64748B]">{t.subtitle}</p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <div className="w-12 h-12 bg-[#E0F2FE] rounded-full flex items-center justify-center mx-auto mb-3">
              <FileText className="h-6 w-6 text-[#1E9BD9]" />
            </div>
            <h3 className="font-semibold text-[#0F172A] mb-1">{t.step1}</h3>
            <p className="text-sm text-[#64748B]">{t.step1Desc}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <div className="w-12 h-12 bg-[#E0F2FE] rounded-full flex items-center justify-center mx-auto mb-3">
              <Camera className="h-6 w-6 text-[#1E9BD9]" />
            </div>
            <h3 className="font-semibold text-[#0F172A] mb-1">{t.step2}</h3>
            <p className="text-sm text-[#64748B]">{t.step2Desc}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <div className="w-12 h-12 bg-[#E0F2FE] rounded-full flex items-center justify-center mx-auto mb-3">
              <User className="h-6 w-6 text-[#1E9BD9]" />
            </div>
            <h3 className="font-semibold text-[#0F172A] mb-1">{t.step3}</h3>
            <p className="text-sm text-[#64748B]">{t.step3Desc}</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-600 p-3 rounded-lg mb-4">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">{t.firstName} *</label>
              <input
                type="text"
                value={userData.first_name}
                onChange={(e) => setUserData({...userData, first_name: e.target.value})}
                className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]"
                placeholder={isRTL ? 'محمد' : 'John'}
                data-testid="first-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">{t.lastName} *</label>
              <input
                type="text"
                value={userData.last_name}
                onChange={(e) => setUserData({...userData, last_name: e.target.value})}
                className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]"
                placeholder={isRTL ? 'أحمد' : 'Doe'}
                data-testid="last-name-input"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-[#0F172A] mb-2">{t.email} *</label>
            <input
              type="email"
              value={userData.email}
              onChange={(e) => setUserData({...userData, email: e.target.value})}
              className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]"
              placeholder="example@email.com"
              dir="ltr"
              data-testid="email-input"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">{t.phone}</label>
              <input
                type="tel"
                value={userData.phone}
                onChange={(e) => setUserData({...userData, phone: e.target.value})}
                className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]"
                placeholder="+971501234567"
                dir="ltr"
                data-testid="phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">{t.idNumber}</label>
              <input
                type="text"
                value={userData.id_number}
                onChange={(e) => setUserData({...userData, id_number: e.target.value})}
                className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]"
                placeholder="784-XXXX-XXXXXXX-X"
                dir="ltr"
                data-testid="id-number-input"
              />
            </div>
          </div>

          <Button 
            onClick={createSession}
            disabled={loading}
            className="w-full btn-primary text-white py-4 text-lg"
            data-testid="start-verification-btn"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            ) : (
              t.startBtn
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
